import { ErrorTag } from "@/components/ErrorTag/ErrorTag";
import { ImportContext } from "@/components/ImportComponents/ImportContext";
import { TemplateColumn } from "@/components/ImportComponents/types";
import { useTrans } from "@/translations";

import React, { useContext, useMemo } from "react";
import { Tooltip } from "react-tooltip";

import { v4 } from "uuid";

import { ValidateCell } from "./ValidateCell";

type Props = {
  sectionKey: string;
  column: TemplateColumn;
};

export const ValidateColumn: React.FC<Props> = ({ sectionKey, column }) => {
  const { t } = useTrans();
  const { validationState, mappingState } = useContext(ImportContext);

  const columnValidation = useMemo(
    () => validationState[sectionKey]?.[column.key],
    [validationState, sectionKey, column],
  );

  const nrErrors = useMemo(
    () => columnValidation?.filter((el) => el.error).length || 0,
    [columnValidation],
  );

  const cellsView = useMemo(
    () =>
      columnValidation?.map((cell, i) => (
        <ValidateCell
          key={`${sectionKey}-${column.key}-${i}`}
          sectionKey={sectionKey}
          cell={cell}
          cellIndex={i}
          column={column}
        />
      )),
    [columnValidation, sectionKey, column],
  );

  const isRequired = useMemo(
    () => column.isRequired || column.isRequiredFunction?.(mappingState),
    [column, mappingState],
  );

  const title = useMemo(
    () => `${t(column.title)} ${isRequired ? " *" : ""}`,
    [column.title, isRequired, t],
  );

  const tooltipUUID = v4();

  return (
    <div className="flex flex-col">
      <div className="border-grey3 sticky top-0 z-9 flex h-10 border-b bg-white p-2.5 text-sm font-medium">
        <div className="truncate" data-tooltip-id={tooltipUUID}>
          <Tooltip id={tooltipUUID} content={title} className="z-10" />
          {title}
        </div>
        {nrErrors > 0 && (
          <div className="ml-2">
            <ErrorTag nrErrors={nrErrors} />
          </div>
        )}
      </div>

      {cellsView}
    </div>
  );
};
